<script setup lang="ts">
import VuePdfEmbed from 'vue-pdf-embed'
import SignArtworkReviewDialog from '@/components/projects/signs/sign-modal/sign-artwork-review/SignArtworkReviewDialog.vue'

interface IProps {
  sign?: any,
  signSides?: number,
  sides?: string[],
  showSignArtworkReviewDialog?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  sign: null,
  signSides: 1,
  sides: () => (['A', 'B', 'C', 'D']),
  showSignArtworkReviewDialog: false,
})

interface CustomEvents {
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:showSignArtworkReviewDialog', value: boolean): void
  (e: 'onClose'): void
  (e: 'onArtworkPdfRendered'): void
}

const emit = defineEmits<CustomEvents>()

const page = ref(1)
const totalPages = ref(1)
const artworkPdf = ref()
const artworkLoading = ref(true)

const isHoveredArtworkBlock = ref(false)

const handleArtworkRendered = () => {
  artworkLoading.value = false
  totalPages.value = artworkPdf.value.pageCount
  emit('onArtworkPdfRendered')
}
const handleArtworkClick = () => {
  emit('update:showSignArtworkReviewDialog', true)
}
</script>

<template>
  <div>
    <SignArtworkReviewDialog
      :model-value="showSignArtworkReviewDialog"
      :sign="sign"
      @update:modelValue="newValue => emit('update:showSignArtworkReviewDialog', newValue)"
    />
    <template v-if="!sign?.artwork_link">
      <div class="pa-2 text-center">
        No Artwork
      </div>
    </template>
    <template v-else>
      <VOverlay
        v-model="artworkLoading"
        class="align-center justify-center"
        contained
        persistent
      >
        <VProgressCircular
          color="primary"
          indeterminate
        />
      </VOverlay>
      <div v-if="!artworkLoading" class="d-flex justify-space-between align-center">
        <div class="d-flex align-center justify-start">
          <a
            target="_blank"
            :href="sign?.artwork_link"
          >
            Download
          </a>
        </div>
        <div
          v-if="sign?.total_annotations_count"
          class="d-flex align-center justify-end"
        >
          Annotations:
          {{ sign.total_annotations_count }}
          <div class="position-relative">
            <VIcon
              size="18"
              icon="tabler-info-circle"
              class="ml-1"
            />
            <VMenu
              activator="parent"
              location="top center"
              open-on-hover
              open-delay="100"
              close-delay="0"
              offset="5"
              class="default-tooltip"
            >
              <span class="px-2">
                Total: {{ sign.total_annotations_count }}<br>
                Unresolved: {{ sign.unresolved_annotations_count }}
              </span>
            </VMenu>
          </div>
        </div>
      </div>
      <div
        class="artwork-block"
        @mouseover="isHoveredArtworkBlock = true"
        @mouseleave="isHoveredArtworkBlock = false"
        @click="handleArtworkClick"
      >
        <VuePdfEmbed
          ref="artworkPdf"
          :page="page"
          :source="sign?.artwork_link"
          @rendered="handleArtworkRendered"
        />
        <transition name="fade">
          <div v-if="isHoveredArtworkBlock" class="overlay">
            <VIcon class="fullscreen-icon" size="48" icon="mdi-fullscreen" />
          </div>
        </transition>
      </div>
      <div class="pdf-pages">
        <template v-for="side in signSides + 1">
          <a
            class="pdf-page"
            :class="{ 'active': side  === page, 'disabled': side > totalPages }"
            @click="side > totalPages ? {} : page = side"
          >
            {{ sides[side - 1] }}
          </a>
        </template>
      </div>
    </template>
  </div>
</template>

<style lang="scss">
.pdf-pages {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  .pdf-page {
    cursor: pointer;
    padding: 2px 8px;
    margin: 0 2px;
    border: 1px solid #DC0000;
    border-radius: 50%;
    &.active {
      background: #DC0000;
      color: #fff;
    }
    &.disabled {
      background: rgba(#DC0000, 0.2);
      color: #fff;
      cursor: not-allowed;
    }
  }
}
.artwork-block {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  transition: box-shadow 0.3s ease;
}

.artwork-block:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s ease;
}

.fullscreen-icon {
  color: white;
  opacity: 0.8;
  transition: transform 0.3s ease;
}

.fullscreen-icon:hover {
  transform: scale(1.2);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
